import { useState, useEffect, useRef } from "react";
import { useParams } from "wouter";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useAppNavigation } from "@/lib/platform";
import { useIsNative, useReview } from "@/lib/platform/PlatformContext";
import { ChevronLeft, ChevronRight, ChevronDown, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast } from "@/components/ux/Toast";
import { 
  useSubmitPostDinnerFeedback,
  useReservation,
  usePostDinnerFeedback
} from "@/lib/api";
import {
  postDinnerQuizQuestions,
  getCurrentCategory
} from "../data/postDinnerQuiz";
import { QuizQuestion } from "@shared/types";
import QuestionRenderer from "@/components/quiz/QuestionRenderer";
import { EventDisplay } from "../components/booking/EventDisplay";
import tracking from "@/lib/tracking";
import { Loader } from '@/components/Loader';

export default function PostDinnerSurvey() {
  const { isLoaded } = useAuth();
  useEffect(() => { tracking.trackFlow('dinner-survey'); }, []);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { navigate } = useAppNavigation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { bookingId } = useParams<{ bookingId: string }>();
  const isNative = useIsNative();
  const review = useReview();

  // Fetch booking details to show event info
  const { data: bookingData } = useReservation(bookingId, {
    enabled: isLoaded && !!bookingId,
    useBackoff: false,
  });

  // Fetch existing feedback
  const { data: existingFeedback, isLoading: feedbackLoading } = usePostDinnerFeedback(bookingId);

  // Submit mutation hook
  const submitFeedback = useSubmitPostDinnerFeedback();

  const quizQuestions = postDinnerQuizQuestions;
  const question = quizQuestions[currentQuestion];
  const isLastQuestion = currentQuestion === quizQuestions.length - 1;
  const currentAnswer = answers[question.id];
  const currentCategory = getCurrentCategory(currentQuestion);

  const canProceed = !question.required || (
    currentAnswer !== undefined && 
    currentAnswer !== '' && 
    (Array.isArray(currentAnswer) ? currentAnswer.length > 0 : true)
  );

  // Auto-redirect if no booking ID
  useEffect(() => {
    if (isLoaded && !bookingId) {
      navigate("/connections");
    }
  }, [isLoaded, bookingId, navigate]);

  // Populate answers from existing feedback when loaded
  useEffect(() => {
    if (existingFeedback) {
      const populatedAnswers: Record<string, any> = {};
      
      // Map feedback fields to answer format
      if (existingFeedback.overallExperience !== null && existingFeedback.overallExperience !== undefined) {
        populatedAnswers.overallExperience = existingFeedback.overallExperience;
      }
      if (existingFeedback.feltWelcomed) {
        populatedAnswers.feltWelcomed = existingFeedback.feltWelcomed;
      }
      
      if (existingFeedback.restaurantSelection) {
        populatedAnswers.restaurantSelection = existingFeedback.restaurantSelection;
      }
      if (existingFeedback.futureAttendance) {
        populatedAnswers.futureAttendance = existingFeedback.futureAttendance;
      }
      if (existingFeedback.recommendationScore !== null && existingFeedback.recommendationScore !== undefined) {
        populatedAnswers.recommendationScore = existingFeedback.recommendationScore;
      }
      if (existingFeedback.notes) {
        populatedAnswers.notes = existingFeedback.notes;
      }
      
      setAnswers(populatedAnswers);
    }
  }, [existingFeedback]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Helper function to check if a question is answered
  const isQuestionAnswered = (questionId: string) => {
    const answer = answers[questionId];
    return answer !== undefined && 
           answer !== '' && 
           (!Array.isArray(answer) || answer.length > 0);
  };

  // Group questions by category
  const questionsByCategory = quizQuestions.reduce((acc, q, index) => {
    const category = getCurrentCategory(index).name;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push({ ...q, index });
    return acc;
  }, {} as Record<string, Array<QuizQuestion & { index: number }>>);

  const handleNext = () => {
    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handleAnswer = (value: any) => {
    setAnswers(prev => ({ ...prev, [question.id]: value }));
  };

  const handleAutoAdvance = () => {
    setTimeout(() => {
      handleNext();
    }, 300);
  };

  const handleBack = () => {
    if (currentQuestion === 0) {
      navigate("/connections");
    } else {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (submitFeedback.isPending || !bookingId) return;
    
    const submissionData = {
      bookingId,
      overallExperience: answers.overallExperience,
      feltWelcomed: answers.feltWelcomed,
      restaurantSelection: answers.restaurantSelection,
      futureAttendance: answers.futureAttendance,
      recommendationScore: answers.recommendationScore,
      notes: answers.notes || '',
    };

    submitFeedback.mutate(submissionData, {
      onSuccess: async () => {
        // Check if user gave perfect ratings (5/5 and 10/10) on native
        const isPerfectExperience = answers.overallExperience === 5;
        const isPerfectRecommendation = answers.recommendationScore === 10;
        
        // Request native review if perfect ratings - native API handles debouncing
        if (isNative && isPerfectExperience && isPerfectRecommendation) {
          review.requestReview();
        }
        
        toast.success("Thank you! Your feedback has been submitted successfully.");
        navigate("/connections");
      },
      onError: (error: any) => {
        console.error("Failed to submit post-dinner quiz:", error);
        toast.error("There was an issue submitting your feedback. Please try again.");
      }
    });
  };

  // Show loading state while data loads
  if (!isLoaded || feedbackLoading) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-screen">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Loading...</h2>
          <p className="text-dark-muted">Just a moment</p>
        </div>
      </div>
    );
  }

  // Redirect if no booking ID
  if (!bookingId) {
    return null;
  }

  if (submitFeedback.isPending) {
    return (
      <div className="flex items-center justify-center bg-dark-bg min-h-screen">
        <div className="text-center">
          <Loader className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Submitting your feedback...</h2>
          <p className="text-dark-muted">Thank you for sharing your experience</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 px-4">
      <div className="max-w-2xl mx-auto pb-16">
        {/* Event Details */}
        {bookingData?.data?.eventTime && (
          <EventDisplay event={bookingData.data.eventTime} />
        )}

        {/* Progress Dots */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-3 justify-center">
            {/* Category Badge with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <motion.button
                key={currentCategory.name}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="inline-flex items-center gap-2 px-4 py-2 btn-ghost rounded-full text-sm font-medium text-primary-800 hover:text-primary-900"
              >
                {currentCategory.name}
                <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 mt-2 w-64 bg-dark-surface rounded-lg shadow-xl border border-dark-border z-50"
                  >
                    <div className="p-2">
                      {Object.entries(questionsByCategory).map(([category, questions]) => {
                        const answeredCount = questions.filter(q => isQuestionAnswered(q.id)).length;
                        const totalCount = questions.length;
                        const isCurrentCategory = currentCategory.name === category;
                        const allAnswered = answeredCount === totalCount;

                        return (
                          <button
                            key={category}
                            onClick={() => {
                              setCurrentQuestion(questions[0].index);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors flex items-center justify-between gap-3 mb-1 last:mb-0 ${
                              isCurrentCategory
                                ? 'bg-primary-500/20 text-primary-500'
                                : 'hover:bg-dark-elevated/50 text-dark-text'
                            }`}
                          >
                            <span className="font-medium">{category}</span>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs ${isCurrentCategory ? 'text-primary-500' : 'text-dark-muted'}`}>
                                {answeredCount}/{totalCount}
                              </span>
                              {allAnswered && (
                                <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <span className="text-sm text-primary-800">
              {currentQuestion + 1}/{quizQuestions.length}
            </span>
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            {Object.entries(questionsByCategory).map(([category, questions]) => (
              <div key={category} className="flex items-center gap-1.5">
                {questions.map((q) => {
                  const isAnswered = isQuestionAnswered(q.id);
                  const isCurrent = currentQuestion === q.index;
                  
                  return (
                    <motion.button
                      key={q.id}
                      onClick={() => setCurrentQuestion(q.index)}
                      animate={{
                        width: isCurrent ? 24 : 8,
                      }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className={`h-2 rounded-full transition-colors cursor-pointer hover:opacity-80 ${
                        isCurrent ? 'bg-primary-800' : isAnswered
                          ? 'bg-primary-500'
                          : 'bg-primary-200'
                      }`}
                      aria-label={`Go to question ${q.index + 1}: ${q.question}`}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="card mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-8">
              {question.question}
            </h2>

            <QuestionRenderer
              question={question as any}
              value={currentAnswer}
              onChange={handleAnswer}
              onFinished={handleAutoAdvance}
            />

          </motion.div>
        </AnimatePresence>

        {/* Navigation Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleBack}
            className="btn btn-ghost px-6 py-3"
          >
            <ChevronLeft className="w-5 h-5 mr-1 inline-block" />
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="btn btn-primary flex-1 px-6 py-3"
          >
            {isLastQuestion ? 'Submit Feedback' : 'Next'}
            {!isLastQuestion && <ChevronRight className="w-5 h-5 ml-1 inline-block" />}
          </button>
        </div>

        {/* Skip indicator for optional questions */}
        {!question.required && !currentAnswer && (
          <p className="text-center text-dark-muted text-sm mt-4">
            This question is optional
          </p>
        )}
      </div>
    </div>
  );
}
